import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../Redux/store';
import { notificationService } from '../../service/FCM/NotificationService';
import { viewOrderDetails } from '../../service/order/orderService';
import { getStoredOrdersFromIndexedDB, removeOrderFromIndexedDB, storeActiveStoreId } from '../../utils/indexedDBUtils';
import { useAppSelector } from '../../Redux/hooks';
import { usePermission } from '../../Redux/SlicesHooks/usePermission';
import { setLastMessage } from '../../Redux/Slices/fcmSlice';
import { addInAppNotification } from '../../Redux/Slices/notificationSlice';
import { triggerOrderToast } from '../OrderNotification/OrderNotificationToast';

const USER_NOTIFICATION_ROLES = new Set([
  'Super Admin',
  'Super User',
  'Support Admin',
  'Fleet Admin',
]);

const RealTimeNotificationHandler: React.FC = () => {
  const { lastMessage } = useSelector((state: RootState) => state.fcm);
  const dispatch = useDispatch();
  const roleID = useSelector((state: RootState) => state?.roleAndPermission?.roleID) || 0;
  const roleName = useSelector((state: RootState) => state?.roleAndPermission?.name) || '';
  const notificationScope = useSelector((state: any) => state?.roleAndPermission?.notification_scope);
  const canViewNotification = usePermission('notification', 'view');
  const permissions = useAppSelector((state) => state?.roleAndPermission?.permissions);
  const isAuthenticated = localStorage.getItem('LOGIN_STATUS') === 'LOGIN';
  const orgCode = process.env.REACT_APP_ORG_CODE;
  const isUserNotificationRole =
    notificationScope === 'user' || USER_NOTIFICATION_ROLES.has(roleName);
  const isRoleEligible = roleID === 3 || roleID === 4 || isUserNotificationRole;
  const isOrgEligible = orgCode === 'tazty' || orgCode === 'valar';
  const canUseFCM = isAuthenticated && isRoleEligible && isOrgEligible;

  // RBAC: check if current role is enabled for order notifications
  const enabledRoleIds = useAppSelector(
    (state) => state.orderNotificationSettings?.enabledRoleIds ?? [3, 4]
  );
  const isRoleEnabledForNotifications = enabledRoleIds.includes(roleID);
  const explicitNotificationPermission = (permissions as any)?.notification?.view;
  const canViewNotificationResolved =
    isUserNotificationRole
      ? true
      :
    explicitNotificationPermission === true
      ? true
      : explicitNotificationPermission === false
        ? false
        : (canViewNotification || isRoleEligible);
  const isRoleEnabledResolved = isUserNotificationRole || isRoleEnabledForNotifications;

  // ✅ Track processed order IDs to prevent duplicate API calls
  const processedOrderIdsRef = useRef<Set<string>>(new Set());
  const MAX_PROCESSED_IDS = 20; // Keep last 20 order IDs
  
  // ✅ Track processed messages to prevent duplicates from service worker forwarding
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const MAX_PROCESSED_MESSAGES = 50; // Keep last 50 messages
  const processedNotificationKeysRef = useRef<Set<string>>(new Set());
  const MAX_PROCESSED_NOTIFICATION_KEYS = 100;

  const getNotificationKey = useCallback((data: any) => {
    const type = data?.type || 'notification';
    const orderId =
      data?.order_id ||
      data?.orderId ||
      data?.order_number ||
      data?.order_code ||
      data?.id ||
      'unknown';
    const status = data?.status || 'unknown';
    return `${type}:${orderId}:${status}`;
  }, []);

  const markNotificationProcessed = useCallback((key: string) => {
    if (processedNotificationKeysRef.current.has(key)) {
      return false;
    }

    processedNotificationKeysRef.current.add(key);
    if (processedNotificationKeysRef.current.size > MAX_PROCESSED_NOTIFICATION_KEYS) {
      const firstKey = Array.from(processedNotificationKeysRef.current)[0];
      processedNotificationKeysRef.current.delete(firstKey);
    }

    return true;
  }, []);

  // Clean up old order IDs periodically (keep set size manageable)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (processedOrderIdsRef.current.size > MAX_PROCESSED_IDS) {
        const idsArray = Array.from(processedOrderIdsRef.current);
        // Remove oldest half
        const toRemove = idsArray.slice(0, Math.floor(idsArray.length / 2));
        toRemove.forEach(id => processedOrderIdsRef.current.delete(id));
        console.log('[RealTimeNotificationHandler] Cleaned up processed order IDs');
      }
    }, 60000); // Every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  // Note: FCM initialization is handled in App.tsx, not here
  // This prevents duplicate initialization calls that cause race conditions

  // Handle background order messages from service worker
  const handleBackgroundOrderMessage = useCallback(async (orderData: any) => {
    console.log('📦 Background order message received, fetching order details:', orderData);

    // ✅ CRITICAL: Ensure ACTIVE_STORE_ID is set from CURRENT_STORE_ID if missing
    // This handles cases where ACTIVE_STORE_ID might not be initialized yet
    const currentActiveStoreId = localStorage.getItem('ACTIVE_STORE_ID');
    const currentStoreId = localStorage.getItem('CURRENT_STORE_ID');
    if (!currentActiveStoreId && currentStoreId) {
      console.log('[RealTimeNotificationHandler] ⚠️ ACTIVE_STORE_ID not set, setting from CURRENT_STORE_ID:', currentStoreId);
      localStorage.setItem('ACTIVE_STORE_ID', currentStoreId);
      // Sync to IndexedDB immediately
      storeActiveStoreId(currentStoreId).catch(err => {
        console.error('[RealTimeNotificationHandler] Failed to sync ACTIVE_STORE_ID to IndexedDB:', err);
      });
    }

    // ✅ ENHANCED: Check if notification is for the active store (for sellers with multiple stores)
    // For multi-store sellers, use ACTIVE_STORE_ID to filter notifications
    
    // ✅ ENHANCED: Check multiple possible locations for store_id in notification payload
    const notificationStoreIdRaw = orderData?.store_id || 
                                   orderData?.storeId || 
                                   orderData?.store?.id || 
                                   null;
    
    // Convert to number with proper validation
    let notificationStoreId: number | null = null;
    if (notificationStoreIdRaw !== null && notificationStoreIdRaw !== undefined && notificationStoreIdRaw !== '') {
      const parsed = typeof notificationStoreIdRaw === 'string' 
        ? parseInt(notificationStoreIdRaw, 10) 
        : Number(notificationStoreIdRaw);
      notificationStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    // ✅ PRIORITY: Use ACTIVE_STORE_ID for notification filtering (multi-store sellers)
    // Fallback to CURRENT_STORE_ID for backward compatibility
    const activeStoreIdRaw = localStorage.getItem('ACTIVE_STORE_ID') || 
                             localStorage.getItem('CURRENT_STORE_ID') || 
                             localStorage.getItem('storeId') || 
                             null;
    
    let activeStoreId: number | null = null;
    if (activeStoreIdRaw !== null && activeStoreIdRaw !== undefined && activeStoreIdRaw !== '') {
      const parsed = parseInt(activeStoreIdRaw, 10);
      activeStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Full orderData:', JSON.stringify(orderData));
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Notification store ID:', notificationStoreId);
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Active store ID:', activeStoreId);
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Raw notification store ID:', notificationStoreIdRaw);
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Raw active store ID:', activeStoreIdRaw);
    
    // ✅ ENHANCED: Also compare as strings for extra safety (backend sends as string)
    const notificationStoreIdStr = notificationStoreIdRaw ? String(notificationStoreIdRaw).trim() : null;
    const activeStoreIdStr = activeStoreIdRaw ? String(activeStoreIdRaw).trim() : null;
    const stringMatch = notificationStoreIdStr && activeStoreIdStr && notificationStoreIdStr === activeStoreIdStr;
    const numberMatch = notificationStoreId !== null && activeStoreId !== null && notificationStoreId === activeStoreId;
    
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - String comparison:', {
      notificationStoreIdStr,
      activeStoreIdStr,
      stringMatch
    });
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Number comparison:', {
      notificationStoreId,
      activeStoreId,
      numberMatch
    });
    const storesMatch = stringMatch || numberMatch;
    console.log('[RealTimeNotificationHandler] 🔍 Background message store check - Final match:', storesMatch);
    
    // ✅ CRITICAL: For multi-store sellers, only show notifications for the active store
    // Compare both as strings AND numbers for maximum compatibility
    // Only block if BOTH store IDs exist AND they DON'T match (as both string and number)
    // If activeStoreId is not set, allow all notifications (backward compatibility)
    if (notificationStoreIdRaw !== null && activeStoreIdRaw !== null) {
      // Both store IDs exist - check if they match (as string or number)
      if (!storesMatch) {
        console.log('[RealTimeNotificationHandler] ⚠️ Background notification is for a different store. Skipping.');
        console.log('[RealTimeNotificationHandler] ⚠️ Notification store:', notificationStoreIdRaw, '(string:', notificationStoreIdStr, ', number:', notificationStoreId, ')');
        console.log('[RealTimeNotificationHandler] ⚠️ Active store:', activeStoreIdRaw, '(string:', activeStoreIdStr, ', number:', activeStoreId, ')');
        console.log('[RealTimeNotificationHandler] ⚠️ String match:', stringMatch, 'Number match:', numberMatch);
        return;
      } else {
        console.log('[RealTimeNotificationHandler] ✅ Background notification matches active store. Proceeding.');
        console.log('[RealTimeNotificationHandler] ✅ Notification store:', notificationStoreIdRaw, 'Active store:', activeStoreIdRaw);
      }
    } else if (activeStoreIdRaw === null) {
      console.log('[RealTimeNotificationHandler] ℹ️ No active store ID set, allowing all notifications (backward compatibility)');
    } else if (notificationStoreIdRaw === null) {
      console.log('[RealTimeNotificationHandler] ℹ️ Background notification missing store_id, allowing it (fallback for compatibility)');
    }
    
    // If no active store ID set, allow all notifications (backward compatibility)
    // If notification missing store_id, allow it (fallback for compatibility)
    // Only block when explicitly different stores

    const orderId = orderData?.order_id?.toString();
    try {
      const normalizedStatus = typeof orderData?.status === 'string' ? orderData.status.toLowerCase() : '';

      // ✅ Skip duplicate pending messages, but always process status changes (billed/cancelled)
      if (
        orderId &&
        processedOrderIdsRef.current.has(orderId) &&
        normalizedStatus === 'pending'
      ) {
        console.log('[RealTimeNotificationHandler] Pending order already processed, skipping:', orderId);
        return;
      }

      if (
        orderData?.type === 'order_update' &&
        normalizedStatus &&
        normalizedStatus !== 'pending'
      ) {
        // Any non-pending status means order was actioned (accepted/billed/cancelled/preparing etc.)
        // Remove from queue — this enables cross-browser sync via FCM
        console.log(
          '[RealTimeNotificationHandler] Background message indicates removal from queue:',
          orderData.order_id,
          orderData.status
        );
        notificationService.removeOrderFromQueue(orderData.order_id);
        return;
      }

      if (!orderData.order_id) {
        console.warn('No order_id in background message, cannot fetch details');
        if (normalizedStatus === 'pending') {
          return;
        }

        // For any other status, ensure the order is no longer present locally
        notificationService.removeOrderFromQueue(orderData.order_id);
        return;
      }

      // ✅ Mark as processing before API call
      if (orderId) {
        processedOrderIdsRef.current.add(orderId);
      }

      // Fetch full order details
      const isLoading = false;
      const response = await viewOrderDetails(orderData.order_id, isLoading);
      const fullOrderData = response.data.data;

      console.log('✅ Background order details fetched, adding to queue');

      // Add to queue using existing functionality
      notificationService.showOrderConfirmationModal(fullOrderData);
    } catch (error) {
      console.error('❌ Failed to fetch order details from background message:', error);

      // ✅ Remove from processed set on error (allow retry)
      if (orderId) {
        processedOrderIdsRef.current.delete(orderId);
      }

      // Fallback: Try to add basic order data to queue
      try {
        const fallbackOrderData = {
          id: orderData.order_id,
          orderId: orderData.order_id,
          customer: { name: 'Customer' },
          order_items: [],
          total_amount: 0,
          status: 'pending',
          timestamp: orderData.timestamp || new Date().toISOString(),
          store_id: orderData.store_id,
        };
        notificationService.showOrderConfirmationModal(fallbackOrderData);
        console.log('Added fallback order data to queue');
      } catch (fallbackError) {
        console.error('Failed to add fallback order to queue:', fallbackError);
      }
    }
  }, []);

  // Listen for service worker messages
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // ✅ CRITICAL: Handler for service worker messages
      // This ensures we catch messages even if the service worker forwards them
      const handler = (event: MessageEvent) => {
        // Only process messages from service worker
        if (!event.data || typeof event.data !== 'object') {
          return;
        }
        
        console.log('[RealTimeNotificationHandler] ========================================');
        console.log('[RealTimeNotificationHandler] ✅✅✅ Service worker message received ✅✅✅');
        console.log('[RealTimeNotificationHandler] Message type:', event?.data?.type);
        console.log('[RealTimeNotificationHandler] Full message data:', event.data);
        console.log('[RealTimeNotificationHandler] Event source:', event.source);
        console.log('[RealTimeNotificationHandler] ========================================');

        if (event?.data?.type === 'NAVIGATE') {
          console.log('[RealTimeNotificationHandler] 🔄 Service worker requested navigation to:', event.data.url);
          window.location.href = event.data.url;
        } else if (event?.data?.type === 'FCM_BACKGROUND_MESSAGE') {
          // ✅ CRITICAL: Handle FCM background message from service worker
          // This ensures messages reach the client even when app is in foreground
          // but message was processed by service worker
          console.log('[RealTimeNotificationHandler] ========================================');
          console.log('[RealTimeNotificationHandler] ✅✅✅ FCM_BACKGROUND_MESSAGE RECEIVED ✅✅✅');
          console.log('[RealTimeNotificationHandler] This means message came through service worker');
          console.log('[RealTimeNotificationHandler] Full event data:', event.data);
          console.log('[RealTimeNotificationHandler] ========================================');
          
          const payload = event.data.payload;
          const orderData = event.data.orderData;
          
          console.log('[RealTimeNotificationHandler] FCM_BACKGROUND_MESSAGE payload:', payload);
          console.log('[RealTimeNotificationHandler] FCM_BACKGROUND_MESSAGE orderData:', orderData);
          console.log('[RealTimeNotificationHandler] Payload data:', payload?.data);
          console.log('[RealTimeNotificationHandler] Order data store_id:', orderData?.store_id);
          console.log('[RealTimeNotificationHandler] Order data order_id:', orderData?.order_id);
          
          // ✅ CRITICAL: Prevent duplicate processing
          // Create a unique message ID from order_id and timestamp
          const orderId = payload?.data?.order_id || orderData?.order_id;
          const messageId = orderId 
            ? `order_${orderId}_${payload?.data?.timestamp || orderData?.timestamp || Date.now()}`
            : `msg_${Date.now()}_${Math.random()}`;
          
          console.log('[RealTimeNotificationHandler] Message ID:', messageId);
          
          if (processedMessagesRef.current.has(messageId)) {
            console.log('[RealTimeNotificationHandler] ⚠️ Duplicate message detected, skipping:', messageId);
            return;
          }
          
          // Add to processed set
          processedMessagesRef.current.add(messageId);
          if (processedMessagesRef.current.size > MAX_PROCESSED_MESSAGES) {
            const firstId = Array.from(processedMessagesRef.current)[0];
            processedMessagesRef.current.delete(firstId);
          }
          
          // ✅ CRITICAL: Dispatch to Redux so it can be processed by the normal flow
          // Use the dispatch hook instead of require() to avoid issues
          if (payload) {
            try {
              console.log('[RealTimeNotificationHandler] ✅ Dispatching payload to Redux...');
              dispatch(setLastMessage(payload));
              console.log('[RealTimeNotificationHandler] ✅✅✅ Dispatched FCM message to Redux successfully ✅✅✅');
              
              // Verify dispatch worked
              setTimeout(() => {
                const state = (window as any).__REDUX_STORE__?.getState?.();
                const lastMsg = state?.fcm?.lastMessage;
                console.log('[RealTimeNotificationHandler] Verification - lastMessage in Redux:', lastMsg ? 'SET ✅' : 'NOT SET ❌');
              }, 100);
            } catch (error) {
              console.error('[RealTimeNotificationHandler] ❌ Failed to dispatch to Redux:', error);
              // Fallback: process directly if Redux dispatch fails
              if (orderData && orderData.type === 'order_update' && orderData.status === 'pending') {
                console.log('[RealTimeNotificationHandler] ⚠️ Fallback: Processing order data directly (Redux dispatch failed)');
                handleBackgroundOrderMessage(orderData);
              }
            }
          } else if (orderData && orderData.type === 'order_update' && orderData.status === 'pending') {
            // Fallback: process order data directly if no payload
            console.log('[RealTimeNotificationHandler] ⚠️ No payload, processing orderData directly');
            handleBackgroundOrderMessage(orderData);
          } else {
            console.log('[RealTimeNotificationHandler] ⚠️ FCM_BACKGROUND_MESSAGE received but no valid payload or orderData');
            console.log('[RealTimeNotificationHandler] Payload:', payload);
            console.log('[RealTimeNotificationHandler] OrderData:', orderData);
          }
        } else if (event?.data?.type === 'ORDER_BACKGROUND_MESSAGE') {
          // Handle background order message - add to queue
          // Sound is automatically handled by showOrderConfirmationModal when queue starts
          console.log('[RealTimeNotificationHandler] ✅ ORDER_BACKGROUND_MESSAGE received');
          handleBackgroundOrderMessage(event.data.orderData);
        } else if (
          event?.data?.messageType === 'push-received' &&
          event?.data?.data?.type === 'order_update'
        ) {
          // Some browsers deliver background payloads using the default push-received event shape
          // Normalize it so we can reuse the same handler
          console.log('[RealTimeNotificationHandler] ✅ push-received message detected');
          handleBackgroundOrderMessage({
            ...event.data.data,
            timestamp: event.data.data.timestamp || new Date().toISOString(),
          });
        } else {
          console.log('[RealTimeNotificationHandler] ⚠️ Unhandled service worker message type:', event?.data?.type);
        }
      };
      
      console.log('[RealTimeNotificationHandler] Setting up service worker message listener...');
      
      // ✅ CRITICAL: Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', handler);
      
      // ✅ CRITICAL: Also listen for messages when service worker becomes available
      const handleControllerChange = () => {
        if (navigator.serviceWorker.controller) {
          console.log('[RealTimeNotificationHandler] ✅ Service worker controller is available');
          // Listener is already attached above; no need to attach again.
        } else {
          console.warn('[RealTimeNotificationHandler] ⚠️ Service worker controller is not available');
        }
      };
      
      // Check if controller is already available
      handleControllerChange();
      
      // Listen for controller changes
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      
      return () => {
        console.log('[RealTimeNotificationHandler] Removing service worker message listener');
        navigator.serviceWorker.removeEventListener('message', handler);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    } else {
      console.warn('[RealTimeNotificationHandler] ⚠️ Service workers not supported');
    }
  }, [handleBackgroundOrderMessage, dispatch]);

  const handleOrderNotification = useCallback(async (notification: any, data: any) => {
    console.log('[RealTimeNotificationHandler] ========================================');

    const notificationKey = getNotificationKey(data);
    if (!markNotificationProcessed(notificationKey)) {
      console.log('[RealTimeNotificationHandler] Duplicate order notification skipped:', notificationKey);
      return;
    }

    const orderNumber =
      data?.order_number ||
      data?.order_code ||
      data?.orderCode ||
      data?.order_id ||
      data?.orderId ||
      'Unknown';
    const storeName =
      data?.store_name ||
      data?.storeName ||
      data?.store_data?.name ||
      data?.storeData?.name ||
      data?.store?.name ||
      '';
    const status = typeof data?.status === 'string' ? data.status.toLowerCase() : '';
    const statusLabel =
      status === 'pending'
        ? 'New order'
        : status === 'cancelled'
          ? 'Order cancelled'
          : status === 'billed'
            ? 'Order accepted'
            : status
              ? `Order ${status.replace(/_/g, ' ')}`
              : 'Order update';
    const orderSummary = `${statusLabel} #${orderNumber}${storeName ? ` from ${storeName}` : ''}`;

    const drawerNotification = {
      ...notification,
      id: notification?.id || `${data?.order_id || data?.orderId || Date.now()}-${data?.status || 'order'}`,
      title: data?.title || orderSummary,
      body: data?.body || notification?.body || orderSummary,
      type: data?.type || 'order_update',
      data,
      timestamp: new Date().toISOString(),
      read: false,
    };

    dispatch(addInAppNotification(drawerNotification));
    triggerOrderToast({
      orderId: data?.order_id || data?.orderId || orderNumber,
      orderCode: orderNumber,
      storeId: data?.store_id || data?.storeId || data?.store?.id,
      storeName,
    });
    notificationService.showNativeNotificationWithSound(data);
    notificationService.playNotificationSound().catch((error) => {
      console.warn('[RealTimeNotificationHandler] Foreground notification sound failed:', error);
    });
    console.log('[RealTimeNotificationHandler] === REAL-TIME ORDER NOTIFICATION ===');
    console.log('[RealTimeNotificationHandler] Order notification received:', data);
    console.log('[RealTimeNotificationHandler] Notification data:', notification);
    console.log('[RealTimeNotificationHandler] ========================================');
    
    // ✅ CRITICAL: Ensure ACTIVE_STORE_ID is set from CURRENT_STORE_ID if missing
    // This handles cases where ACTIVE_STORE_ID might not be initialized yet
    const currentActiveStoreId = localStorage.getItem('ACTIVE_STORE_ID');
    const currentStoreId = localStorage.getItem('CURRENT_STORE_ID');
    const storeId = localStorage.getItem('storeId');
    
    console.log('[RealTimeNotificationHandler] Current localStorage values:');
    console.log('[RealTimeNotificationHandler] - ACTIVE_STORE_ID:', currentActiveStoreId);
    console.log('[RealTimeNotificationHandler] - CURRENT_STORE_ID:', currentStoreId);
    console.log('[RealTimeNotificationHandler] - storeId:', storeId);
    
    if (!currentActiveStoreId && currentStoreId) {
      console.log('[RealTimeNotificationHandler] ⚠️ ACTIVE_STORE_ID not set, setting from CURRENT_STORE_ID:', currentStoreId);
      localStorage.setItem('ACTIVE_STORE_ID', currentStoreId);
      // Sync to IndexedDB immediately
      storeActiveStoreId(currentStoreId).catch(err => {
        console.error('[RealTimeNotificationHandler] Failed to sync ACTIVE_STORE_ID to IndexedDB:', err);
      });
    } else if (!currentActiveStoreId && storeId) {
      console.log('[RealTimeNotificationHandler] ⚠️ ACTIVE_STORE_ID not set, setting from storeId:', storeId);
      localStorage.setItem('ACTIVE_STORE_ID', storeId);
      // Sync to IndexedDB immediately
      storeActiveStoreId(storeId).catch(err => {
        console.error('[RealTimeNotificationHandler] Failed to sync ACTIVE_STORE_ID to IndexedDB:', err);
      });
    }
    
    // ✅ ENHANCED: Check if notification is for the active store (for sellers with multiple stores)
    // For multi-store sellers, use ACTIVE_STORE_ID to filter notifications
    // This ensures notifications (popup and sound) only trigger for the active store
    
    // ✅ ENHANCED: Check multiple possible locations for store_id in notification payload
    // FCM data fields are always strings, so we need to handle both string and number formats
    const notificationStoreIdRaw = data?.store_id || 
                                   data?.storeId || 
                                   data?.store?.id || 
                                   null;
    
    // Convert to number for comparison (handle both string "475" and number 475)
    // Use parseInt with radix 10 to avoid NaN issues, then validate
    let notificationStoreId: number | null = null;
    if (notificationStoreIdRaw !== null && notificationStoreIdRaw !== undefined && notificationStoreIdRaw !== '') {
      const parsed = typeof notificationStoreIdRaw === 'string' 
        ? parseInt(notificationStoreIdRaw, 10) 
        : Number(notificationStoreIdRaw);
      notificationStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    // ✅ PRIORITY: Use ACTIVE_STORE_ID for notification filtering (multi-store sellers)
    // Fallback to CURRENT_STORE_ID for backward compatibility
    // Handle both string and number formats from localStorage
    const activeStoreIdRaw = localStorage.getItem('ACTIVE_STORE_ID') || 
                             localStorage.getItem('CURRENT_STORE_ID') || 
                             localStorage.getItem('storeId') || 
                             null;
    
    let activeStoreId: number | null = null;
    if (activeStoreIdRaw !== null && activeStoreIdRaw !== undefined && activeStoreIdRaw !== '') {
      const parsed = parseInt(activeStoreIdRaw, 10);
      activeStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Full data object:', data);
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Data keys:', data ? Object.keys(data) : 'no data');
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Raw notification store ID:', notificationStoreIdRaw, '(type:', typeof notificationStoreIdRaw, ')');
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Notification store ID (converted):', notificationStoreId);
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Raw active store ID:', activeStoreIdRaw, '(type:', typeof activeStoreIdRaw, ')');
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Active store ID (converted):', activeStoreId);
    
    // ✅ ENHANCED: Also compare as strings for extra safety (backend sends as string)
    const notificationStoreIdStr = notificationStoreIdRaw ? String(notificationStoreIdRaw).trim() : null;
    const activeStoreIdStr = activeStoreIdRaw ? String(activeStoreIdRaw).trim() : null;
    const stringMatch = notificationStoreIdStr && activeStoreIdStr && notificationStoreIdStr === activeStoreIdStr;
    const numberMatch = notificationStoreId !== null && activeStoreId !== null && notificationStoreId === activeStoreId;
    
    console.log('[RealTimeNotificationHandler] 🔍 Store check - String comparison:', {
      notificationStoreIdStr,
      activeStoreIdStr,
      stringMatch
    });
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Number comparison:', {
      notificationStoreId,
      activeStoreId,
      numberMatch
    });
    console.log('[RealTimeNotificationHandler] 🔍 Store check - Final match:', stringMatch || numberMatch);
    
    // ✅ CRITICAL: For multi-store sellers, only show notifications for the active store
    // Compare both as strings AND numbers for maximum compatibility
    // Only block if BOTH store IDs exist AND they DON'T match (as both string and number)
    // If activeStoreId is not set, allow all notifications (backward compatibility for single-store sellers)
    
    // ✅ ENHANCED: More lenient matching - if either string or number matches, allow it
    const storesMatch = stringMatch || numberMatch;
    
    if (notificationStoreIdRaw !== null && activeStoreIdRaw !== null) {
      // Both store IDs exist - check if they match (as string or number)
      if (!storesMatch) {
        console.log('[RealTimeNotificationHandler] ⚠️ BLOCKED: Notification is for a different store. Skipping.');
        console.log('[RealTimeNotificationHandler] ⚠️ Notification store:', notificationStoreIdRaw, '(string:', notificationStoreIdStr, ', number:', notificationStoreId, ')');
        console.log('[RealTimeNotificationHandler] ⚠️ Active store:', activeStoreIdRaw, '(string:', activeStoreIdStr, ', number:', activeStoreId, ')');
        console.log('[RealTimeNotificationHandler] ⚠️ String match:', stringMatch, 'Number match:', numberMatch);
        return;
      } else {
        console.log('[RealTimeNotificationHandler] ✅ ALLOWED: Notification matches active store. Proceeding.');
        console.log('[RealTimeNotificationHandler] ✅ Notification store:', notificationStoreIdRaw, 'Active store:', activeStoreIdRaw);
        console.log('[RealTimeNotificationHandler] ✅ Match confirmed - proceeding with notification');
      }
    } else if (activeStoreIdRaw === null) {
      // No active store ID set - allow all notifications (backward compatibility for single-store sellers)
      console.log('[RealTimeNotificationHandler] ℹ️ ALLOWED: No active store ID set, allowing all notifications (backward compatibility)');
    } else if (notificationStoreIdRaw === null) {
      // Notification missing store_id - allow it (fallback for compatibility)
      console.log('[RealTimeNotificationHandler] ℹ️ ALLOWED: Notification missing store_id, allowing it (fallback for compatibility)');
    }
    
    // If no active store ID set, allow all notifications (backward compatibility)
    // If notification missing store_id, allow it (fallback for compatibility)
    // Only block when explicitly different stores
    
    const orderId = data?.order_id?.toString();
    const normalizedStatus = typeof data?.status === 'string' ? data.status.toLowerCase() : '';

    // ✅ Skip duplicate pending messages, but always process status changes (billed/cancelled)
    if (
      orderId &&
      processedOrderIdsRef.current.has(orderId) &&
      normalizedStatus === 'pending'
    ) {
      console.log('[RealTimeNotificationHandler] Pending order already processed, skipping:', orderId);
      return;
    }

    if (
      data?.type === 'order_update' &&
      normalizedStatus &&
      normalizedStatus !== 'pending'
    ) {
      // Any non-pending status means order was actioned (accepted/billed/cancelled/preparing etc.)
      // Remove from queue — this enables cross-browser sync via FCM
      console.log(
        'Order status update indicates removal from queue:',
        data.order_id,
        data.status
      );
      notificationService.removeOrderFromQueue(data.order_id);
      return;
    }

    if (data?.status === 'pending' && data?.type === 'order_update') {
      try {
        // ✅ Mark as processing before API call
        if (orderId) {
          processedOrderIdsRef.current.add(orderId);
        }

        console.log('Fetching order details for order ID:', data.order_id);
        let isLoading = false;
        const response = await viewOrderDetails(data.order_id, isLoading);
        console.log('Order details fetched successfully:', response.data.data);

        // Pass the full API response directly to the modal
        const orderData = response.data.data;

        // ✅ ENHANCED: Double-check store ID from fetched order data (for sellers with multiple stores)
        // For multi-store sellers, use ACTIVE_STORE_ID to filter notifications
        const fetchedOrderStoreIdRaw = orderData?.store_id || 
                                       orderData?.store?.id || 
                                       orderData?.store_data?.id || 
                                       null;
        
        // Convert to number with proper validation
        let fetchedOrderStoreId: number | null = null;
        if (fetchedOrderStoreIdRaw !== null && fetchedOrderStoreIdRaw !== undefined && fetchedOrderStoreIdRaw !== '') {
          const parsed = typeof fetchedOrderStoreIdRaw === 'string' 
            ? parseInt(fetchedOrderStoreIdRaw, 10) 
            : Number(fetchedOrderStoreIdRaw);
          fetchedOrderStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
        }
        
        // ✅ PRIORITY: Use ACTIVE_STORE_ID for notification filtering (multi-store sellers)
        // Fallback to CURRENT_STORE_ID for backward compatibility
        const activeStoreIdRaw = localStorage.getItem('ACTIVE_STORE_ID') || 
                                 localStorage.getItem('CURRENT_STORE_ID') || 
                                 localStorage.getItem('storeId') || 
                                 null;
        
        let activeStoreId: number | null = null;
        if (activeStoreIdRaw !== null && activeStoreIdRaw !== undefined && activeStoreIdRaw !== '') {
          const parsed = parseInt(activeStoreIdRaw, 10);
          activeStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
        }
        
        console.log('[RealTimeNotificationHandler] Fetched order store check - Order store:', fetchedOrderStoreId, 'Active store:', activeStoreId);
        
        // ✅ CRITICAL: For multi-store sellers, only show notifications for the active store
        // Only block if BOTH store IDs exist AND are valid numbers AND they DON'T match
        if (fetchedOrderStoreId !== null && activeStoreId !== null && fetchedOrderStoreId !== activeStoreId) {
          console.log('[RealTimeNotificationHandler] ⚠️ Fetched order is for a different store. Skipping. Order store:', fetchedOrderStoreId, 'Active store:', activeStoreId);
          return;
        }
        
        // If no active store ID set, allow all orders (backward compatibility)
        // If order missing store_id, allow it (fallback for compatibility)

        console.log('=== ORDER DATA FOR MODAL ===');
        console.log('Full API response:', orderData);
        console.log('Order ID:', orderData.id);
        console.log('Order Store ID:', fetchedOrderStoreId);
        console.log('Active Store ID:', activeStoreId);
        console.log('Customer:', orderData.customer);
        console.log('Order Items:', orderData.order_items);
        console.log('Total Amount:', orderData.total_amount);
        console.log('Order Date:', orderData.order_date);
        console.log('============================');

        // Show order confirmation modal (global sound will be handled by the service)
        notificationService.showOrderConfirmationModal(orderData);

        console.log('Order confirmation modal triggered successfully');
      } catch (error) {
        console.error('Failed to fetch order details:', error);

        // ✅ Remove from processed set on error (allow retry)
        if (orderId) {
          processedOrderIdsRef.current.delete(orderId);
        }

        // Fallback: Show modal with basic data if API fails
        const fallbackOrderData = {
          orderId: data.order_id,
          customerName: 'Customer',
          items: [{ name: 'Order Item', quantity: 1, price: 0 }],
          total: 0,
          status: 'pending',
          timestamp: new Date().toISOString(),
          orderDetails: data
        };

        console.log('Using fallback order data:', fallbackOrderData);
        notificationService.showOrderConfirmationModal(fallbackOrderData);
      }
    } else {
      console.log('Order notification not for pending status or wrong type:', {
        status: data?.status,
        type: data?.type
      });
    }
  }, [dispatch, getNotificationKey, markNotificationProcessed]);

  const handleForegroundMessage = useCallback((payload: any) => {
    console.log('[RealTimeNotificationHandler] ========================================');
    console.log('[RealTimeNotificationHandler] ✅✅✅ Processing foreground FCM message ✅✅✅');
    console.log('[RealTimeNotificationHandler] Full payload:', payload);
    console.log('[RealTimeNotificationHandler] Payload data:', payload?.data);
    console.log('[RealTimeNotificationHandler] Payload notification:', payload?.notification);
    console.log('[RealTimeNotificationHandler] ========================================');

    const { data, notification } = payload;

    // ✅ CRITICAL: Log permission states BEFORE blocking
    console.log('[RealTimeNotificationHandler] Permission check - canViewNotification (hook):', canViewNotification);
    console.log('[RealTimeNotificationHandler] Permission check - explicitNotificationPermission:', explicitNotificationPermission);
    console.log('[RealTimeNotificationHandler] Permission check - canViewNotificationResolved:', canViewNotificationResolved);
    console.log('[RealTimeNotificationHandler] Permission check - isRoleEnabledForNotifications:', isRoleEnabledResolved);
    console.log('[RealTimeNotificationHandler] Permission check - roleID:', roleID);
    console.log('[RealTimeNotificationHandler] Permission check - enabledRoleIds:', enabledRoleIds);
    
    // Permission gate: skip if notification.view is explicitly disabled
    if (!canViewNotificationResolved) {
      console.log('[RealTimeNotificationHandler] ⚠️⚠️⚠️ BLOCKED: notification.view permission is false — ignoring FCM message ⚠️⚠️⚠️');
      console.log('[RealTimeNotificationHandler] ⚠️ THIS IS WHY NOTIFICATIONS ARE NOT WORKING!');
      console.log('[RealTimeNotificationHandler] ⚠️ Fix: Ensure notification.view permission is enabled for this role');
      return;
    }

    // RBAC gate: skip if role is not enabled for notifications
    if (!isRoleEnabledResolved) {
      console.log('[RealTimeNotificationHandler] ⚠️⚠️⚠️ BLOCKED: Role not enabled for order notifications — ignoring FCM message ⚠️⚠️⚠️');
      console.log('[RealTimeNotificationHandler] ⚠️ Role ID:', roleID, 'Enabled roles:', enabledRoleIds);
      console.log('[RealTimeNotificationHandler] ⚠️ THIS IS WHY NOTIFICATIONS ARE NOT WORKING!');
      console.log('[RealTimeNotificationHandler] ⚠️ Fix: Ensure role', roleID, 'is in enabledRoleIds array');
      return;
    }
    
    console.log('[RealTimeNotificationHandler] ✅ Permission checks passed - proceeding with notification processing');

    console.log('[RealTimeNotificationHandler] Extracted data:', data);
    console.log('[RealTimeNotificationHandler] Extracted notification:', notification);

    const notificationData = {
      id: Date.now().toString(),
      title: notification?.title || 'New Notification',
      body: notification?.body || 'You have a new message',
      type: data?.type || 'general',
      data: data,
      timestamp: new Date().toISOString(),
      read: false,
    };

    console.log('[RealTimeNotificationHandler] Notification type:', data?.type);
    
    switch (data?.type) {
      case 'order_update':
        console.log('[RealTimeNotificationHandler] ✅ order_update type detected, calling handleOrderNotification');
        handleOrderNotification(notificationData, data);
        break;
      default:
        console.log('[RealTimeNotificationHandler] ⚠️ Unhandled notification type:', data?.type);
        notificationService.playNotificationSound();
    }
  }, [handleOrderNotification, isRoleEnabledResolved, canViewNotification, canViewNotificationResolved, explicitNotificationPermission, roleID, enabledRoleIds]);

  // Load stored orders from IndexedDB on app startup
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadStoredOrders = async () => {
      try {
        console.log('[RealTimeNotificationHandler] Loading stored orders from IndexedDB...');
        const storedOrders = await getStoredOrdersFromIndexedDB();

        if (storedOrders.length > 0) {
          console.log(`[RealTimeNotificationHandler] Found ${storedOrders.length} stored order(s), processing...`);

          // Process each stored order sequentially to avoid overwhelming the queue
          for (const storedOrder of storedOrders) {
            try {
              if (!storedOrder.order_id) {
                console.warn('[RealTimeNotificationHandler] Stored order missing order_id, skipping');
                continue;
              }

              // Fetch full order details
              const isLoading = false;
              const response = await viewOrderDetails(storedOrder.order_id, isLoading);
              const orderData = response.data.data;

              // ✅ ENHANCED: Check store ID from fetched order data (for sellers with multiple stores)
              // For multi-store sellers, use ACTIVE_STORE_ID to filter notifications
              const fetchedOrderStoreIdRaw = orderData?.store_id || 
                                             orderData?.store?.id || 
                                             orderData?.store_data?.id || 
                                             null;
              
              // Convert to number with proper validation
              let fetchedOrderStoreId: number | null = null;
              if (fetchedOrderStoreIdRaw !== null && fetchedOrderStoreIdRaw !== undefined && fetchedOrderStoreIdRaw !== '') {
                const parsed = typeof fetchedOrderStoreIdRaw === 'string' 
                  ? parseInt(fetchedOrderStoreIdRaw, 10) 
                  : Number(fetchedOrderStoreIdRaw);
                fetchedOrderStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
              }
              
              // ✅ PRIORITY: Use ACTIVE_STORE_ID for notification filtering (multi-store sellers)
              // Fallback to CURRENT_STORE_ID for backward compatibility
              const activeStoreIdRaw = localStorage.getItem('ACTIVE_STORE_ID') || 
                                       localStorage.getItem('CURRENT_STORE_ID') || 
                                       localStorage.getItem('storeId') || 
                                       null;
              
              let activeStoreId: number | null = null;
              if (activeStoreIdRaw !== null && activeStoreIdRaw !== undefined && activeStoreIdRaw !== '') {
                const parsed = parseInt(activeStoreIdRaw, 10);
                activeStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
              }
              
              console.log('[RealTimeNotificationHandler] Stored order store check - Order store:', fetchedOrderStoreId, 'Active store:', activeStoreId);
              
              // ✅ CRITICAL: For multi-store sellers, only show notifications for the active store
              // Only block if BOTH store IDs exist AND are valid numbers AND they DON'T match
              if (fetchedOrderStoreId !== null && activeStoreId !== null && fetchedOrderStoreId !== activeStoreId) {
                console.log('[RealTimeNotificationHandler] ⚠️ Stored order is for a different store. Skipping. Order store:', fetchedOrderStoreId, 'Active store:', activeStoreId);
                // Remove from IndexedDB since it's for a different store
                await removeOrderFromIndexedDB(storedOrder.order_id);
                continue;
              }
              
              // If no active store ID set, allow all stored orders (backward compatibility)
              // If order missing store_id, allow it (fallback for compatibility)

              // Add to queue
              notificationService.showOrderConfirmationModal(orderData);

              // Remove from IndexedDB after successful processing
              await removeOrderFromIndexedDB(storedOrder.order_id);
              console.log(`[RealTimeNotificationHandler] Processed and removed stored order: ${storedOrder.order_id}`);

              // Small delay between orders to prevent queue flooding
              await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
              console.error(`[RealTimeNotificationHandler] Failed to process stored order ${storedOrder.order_id}:`, error);
              // Keep order in IndexedDB if processing failed - it will be retried next time
            }
          }

          console.log('[RealTimeNotificationHandler] Finished loading stored orders');
        } else {
          console.log('[RealTimeNotificationHandler] No stored orders found in IndexedDB');
        }
      } catch (error) {
        console.error('[RealTimeNotificationHandler] Error loading stored orders from IndexedDB:', error);
      }
    };

    // Load stored orders after a short delay to ensure app is fully initialized
    const timeoutId = setTimeout(() => {
      loadStoredOrders();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated]);

  // ✅ DIAGNOSTIC: Log component state on mount and when dependencies change
  useEffect(() => {
    console.log('[RealTimeNotificationHandler] ========================================');
    console.log('[RealTimeNotificationHandler] Component State Check:');
    console.log('[RealTimeNotificationHandler] - canUseFCM:', canUseFCM);
    console.log('[RealTimeNotificationHandler] - isAuthenticated:', isAuthenticated);
    console.log('[RealTimeNotificationHandler] - canViewNotification:', canViewNotification);
    console.log('[RealTimeNotificationHandler] - isRoleEnabledForNotifications:', isRoleEnabledForNotifications);
    console.log('[RealTimeNotificationHandler] - roleID:', roleID);
    console.log('[RealTimeNotificationHandler] - orgCode:', orgCode);
    console.log('[RealTimeNotificationHandler] - ACTIVE_STORE_ID:', localStorage.getItem('ACTIVE_STORE_ID'));
    console.log('[RealTimeNotificationHandler] - CURRENT_STORE_ID:', localStorage.getItem('CURRENT_STORE_ID'));
    console.log('[RealTimeNotificationHandler] ========================================');
  }, [canUseFCM, isAuthenticated, canViewNotification, isRoleEnabledForNotifications, roleID, orgCode]);

  useEffect(() => {
    // Handle new FCM messages (foreground notifications)
    console.log('[RealTimeNotificationHandler] ========================================');
    console.log('[RealTimeNotificationHandler] useEffect triggered for lastMessage');
    console.log('[RealTimeNotificationHandler] lastMessage:', lastMessage);
    console.log('[RealTimeNotificationHandler] lastMessage type:', typeof lastMessage);
    console.log('[RealTimeNotificationHandler] lastMessage is null?', lastMessage === null);
    console.log('[RealTimeNotificationHandler] lastMessage is undefined?', lastMessage === undefined);
    console.log('[RealTimeNotificationHandler] canUseFCM:', canUseFCM);
    console.log('[RealTimeNotificationHandler] isAuthenticated:', isAuthenticated);
    console.log('[RealTimeNotificationHandler] canViewNotification:', canViewNotification);
    console.log('[RealTimeNotificationHandler] isRoleEnabledForNotifications:', isRoleEnabledForNotifications);
    console.log('[RealTimeNotificationHandler] ========================================');
    
    // ✅ CRITICAL: Prevent duplicate processing from Redux updates
    if (lastMessage) {
      const messageId = lastMessage?.data
        ? getNotificationKey(lastMessage.data)
        : `msg_${lastMessage?.messageId || lastMessage?.collapseKey || 'unknown'}`;
      
      if (processedMessagesRef.current.has(messageId)) {
        console.log('[RealTimeNotificationHandler] ⚠️ Duplicate lastMessage detected, skipping:', messageId);
        return;
      }
      
      // Add to processed set
      processedMessagesRef.current.add(messageId);
      if (processedMessagesRef.current.size > MAX_PROCESSED_MESSAGES) {
        const firstId = Array.from(processedMessagesRef.current)[0];
        processedMessagesRef.current.delete(firstId);
      }
      
      console.log('[RealTimeNotificationHandler] ✅✅✅ lastMessage EXISTS - PROCESSING ✅✅✅');
      console.log('[RealTimeNotificationHandler] lastMessage data:', lastMessage.data);
      console.log('[RealTimeNotificationHandler] lastMessage notification:', lastMessage.notification);
      console.log('[RealTimeNotificationHandler] Calling handleForegroundMessage...');
      
      try {
        handleForegroundMessage(lastMessage);
        console.log('[RealTimeNotificationHandler] ✅ handleForegroundMessage completed');
      } catch (error) {
        console.error('[RealTimeNotificationHandler] ❌ Error in handleForegroundMessage:', error);
        // Remove from processed set on error so it can be retried
        processedMessagesRef.current.delete(messageId);
      }
    } else {
      console.log('[RealTimeNotificationHandler] ⚠️ lastMessage is null/undefined - no message to process');
      console.log('[RealTimeNotificationHandler] This could mean:');
      console.log('[RealTimeNotificationHandler] 1. No FCM message has been received yet');
      console.log('[RealTimeNotificationHandler] 2. Message was not dispatched to Redux');
      console.log('[RealTimeNotificationHandler] 3. Redux state was cleared');
    }
  }, [lastMessage, handleForegroundMessage, canUseFCM, isAuthenticated, canViewNotification, isRoleEnabledForNotifications, dispatch, getNotificationKey]);

  // Individual notification handlers removed - only order notifications are handled

  // System notification function removed - only OrderConfirmationModal is used

  // Navigation functions removed - no longer needed since system notifications are removed

  return null;
};

export default RealTimeNotificationHandler;
